import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Trash2, X, History, Plus, ChevronLeft, MessageSquare, RotateCcw } from 'lucide-react';
import { ButtonGroup } from '@/components/ui/button-group';
import { Button } from '@/components/ui/button';
import { useSidebar } from '@/components/ui/sidebar';
import { useAuth } from '@/context/AuthContext';
import { useBusinessContext } from '@/context/BusinessContext';
import { useIsMobile } from '@/hooks/ui/use-mobile';
import { cn } from '@/lib/core/utils';
import { AI_ASSISTANT } from '@/lib/ai-chat/constants';
import { AIMessageList } from './AIMessageList';
import { AIMessageInput } from './AIMessageInput';
import { useAIChat } from './useAIChat';
import type { AIChatPanelProps, AIToolCall, PendingTool } from './types';

// Format relative time for conversation list
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

export const AIChatPanel: React.FC<AIChatPanelProps> = ({
  businessId,
  className,
}) => {
  const { aiModeActive, setAiModeActive, state } = useSidebar();
  const { session } = useAuth();
  const { selectedBusiness } = useBusinessContext();
  const isMobile = useIsMobile();
  const inputRef = useRef<{ setInput: (text: string) => void } | null>(null);
  const {
    messages,
    isLoading,
    error,
    sendMessage,
    clearMessages,
    confirmTools,
    sendQuestionAnswers,
    conversationSummaries,
    activeConversationId,
    createNewConversation,
    switchConversation,
    deleteConversation,
    usage,
    stopGeneration,
    retryLastMessage,
    canRetry,
    wasStopped,
  } = useAIChat(businessId, session?.access_token);

  // View state: 'chat' or 'history'
  const [view, setView] = useState<'chat' | 'history'>('chat');

  // Resizable height state - persist to localStorage
  const [height, setHeight] = useState(() => {
    try {
      const stored = localStorage.getItem('ai_chat_height');
      return stored ? parseInt(stored, 10) : 500;
    } catch {
      return 500;
    }
  });
  const [isResizing, setIsResizing] = useState(false);

  // Get current conversation name
  const currentConversation = conversationSummaries.find(c => c.id === activeConversationId);
  const conversationTitle = currentConversation?.title || 'New Chat';

  // Handle resize via mouse events
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  // Double-click to snap to middle
  const handleDoubleClick = () => {
    const newHeight = Math.round(window.innerHeight * 0.5);
    setHeight(newHeight);
    try {
      localStorage.setItem('ai_chat_height', String(newHeight));
    } catch {
      // Ignore localStorage errors (e.g., private browsing)
    }
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newHeight = window.innerHeight - e.clientY;
      setHeight(Math.max(200, Math.min(newHeight, window.innerHeight * 0.9)));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Save height to localStorage when done resizing
  useEffect(() => {
    if (isResizing) return;
    try {
      localStorage.setItem('ai_chat_height', String(height));
    } catch {
      // Ignore localStorage errors
    }
  }, [height, isResizing]);

  // Handle selecting a conversation from history
  const handleSelectConversation = (id: string) => {
    switchConversation(id);
    setView('chat');
  };

  // Handle creating a new conversation
  const handleNewChat = () => {
    createNewConversation();
    setView('chat');
  };

  // Handle deleting a conversation
  const handleDeleteConversation = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    deleteConversation(id);
  };

  // Handle reverting a create operation
  const handleRevert = useCallback(async (toolCall: AIToolCall) => {
    const resourceId = toolCall.result?.shift?.id || toolCall.result?.employee?.id;
    const resourceType = toolCall.result?.shift ? 'shift' : 'employee';

    if (!resourceId) {
      console.error('Cannot revert: no resource ID found in tool result');
      return;
    }

    // Send a message to delete the resource
    await sendMessage(`Please delete the ${resourceType} with ID ${resourceId} that was just created.`);
  }, [sendMessage]);

  // Handle suggestion click from welcome screen
  const handleSuggestionClick = useCallback((text: string) => {
    sendMessage(text);
  }, [sendMessage]);

  // Don't render on mobile - use MobileAIChatOverlay instead
  if (isMobile) return null;

  // Don't render if AI mode is not active
  if (!aiModeActive) return null;

  // Don't render in collapsed state to avoid layout issues
  if (state === 'collapsed') return null;

  return (
    <div
      className={cn(
        'flex flex-col border-t bg-background',
        className
      )}
      style={{ height: `${height}px` }}
    >
      {/* Resize handle */}
      <div
        className={cn(
          'h-1.5 cursor-ns-resize bg-border transition-colors hover:bg-primary/50',
          isResizing && 'bg-primary/50'
        )}
        onMouseDown={handleResizeStart}
        onDoubleClick={handleDoubleClick}
      />

      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30">
        <div className="flex items-center gap-2 min-w-0">
          {view === 'history' ? (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 shrink-0"
                onClick={() => setView('chat')}
                title="Back to chat"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium">Chat History</span>
            </>
          ) : (
            <>
              <div className="w-6 h-6 rounded-full flex items-center justify-center ring-1 ring-border shrink-0">
                <img
                  src="/media/tag-innovation-logo.png"
                  alt={AI_ASSISTANT.name}
                  className="h-3.5 w-3.5 rounded-sm"
                />
              </div>
              <span className="text-sm font-medium truncate">{conversationTitle}</span>
              {usage && usage.totalTokens !== undefined && (
                <span
                  className="text-xs text-muted-foreground ml-1 shrink-0"
                  title={`${usage.totalTokens.toLocaleString()} / ${usage.contextWindow.toLocaleString()} tokens`}
                >
                  {usage.contextUsedPercent}%
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {view === 'chat' && (
            <ButtonGroup>
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                onClick={handleNewChat}
                title="New chat"
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
              {conversationSummaries.length > 0 && (
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setView('history')}
                  title="Chat history"
                >
                  <History className="h-3.5 w-3.5" />
                </Button>
              )}
              {messages.length > 0 && (
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  onClick={clearMessages}
                  title="Clear chat"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </ButtonGroup>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setAiModeActive(false)}
            title="Close AI Assistant"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {view === 'history' ? (
        /* History View */
        <div className="flex-1 overflow-y-auto">
          {conversationSummaries.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-4">
              <MessageSquare className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">No chat history yet</p>
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {conversationSummaries.map((conv) => {
                const isActive = conv.id === activeConversationId;
                return (
                  <div
                    key={conv.id}
                    onClick={() => handleSelectConversation(conv.id)}
                    className={cn(
                      'group flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors',
                      'hover:bg-muted',
                      isActive && 'bg-primary/10 border-l-2 border-primary'
                    )}
                  >
                    <MessageSquare className={cn(
                      'h-4 w-4 shrink-0',
                      isActive ? 'text-primary' : 'text-muted-foreground'
                    )} />
                    <div className="flex-1 min-w-0">
                      <p className={cn(
                        'text-sm truncate',
                        isActive ? 'font-medium' : ''
                      )}>{conv.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {conv.messageCount} messages · {formatRelativeTime(conv.updatedAt)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 opacity-30 hover:opacity-100 transition-opacity"
                      onClick={(e) => handleDeleteConversation(e, conv.id)}
                      title="Delete conversation"
                    >
                      <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        /* Chat View */
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3">
            <AIMessageList
              messages={messages}
              isLoading={isLoading}
              onRevert={handleRevert}
              onConfirmTools={confirmTools}
              onSuggestionClick={handleSuggestionClick}
              businessLogoUrl={selectedBusiness?.favicon_url}
              businessId={businessId}
              onQuestionsSubmit={sendQuestionAnswers}
            />
          </div>

          {/* Error display with retry button */}
          {(error || wasStopped) && (
            <div className="px-3 py-2 bg-destructive/10 text-destructive text-xs flex items-center justify-between gap-2">
              <span>{error || 'Generation stopped'}</span>
              {canRetry && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={retryLastMessage}
                  className="h-6 px-2 text-xs"
                >
                  <RotateCcw className="h-3 w-3 mr-1" />
                  Retry
                </Button>
              )}
            </div>
          )}

          {/* Input */}
          <AIMessageInput
            onSend={sendMessage}
            onStop={stopGeneration}
            disabled={isLoading || !businessId}
            isLoading={isLoading}
            placeholder={
              businessId
                ? 'Ask about your business data...'
                : 'Select a business first'
            }
          />
        </>
      )}
    </div>
  );
};

export default AIChatPanel;
